import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { AiOutlinePlus, AiOutlineClose, AiOutlineEdit, AiOutlineCopy, AiOutlinePoweroff, AiOutlineLoading3Quarters } from 'react-icons/ai';
import { BsList } from 'react-icons/bs';
import { FiFileText, FiAlignLeft, FiMail, FiPaperclip, FiType, FiList, FiSave, FiLoader, FiCheck, FiX, FiExternalLink, FiSettings, FiLayers, FiCircle } from 'react-icons/fi';
import { FaCircle } from 'react-icons/fa';
import { GoArrowLeft } from "react-icons/go";
import { motion } from "framer-motion";
import { OrganizationSelect } from "@/components/OrganizationSelect";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { z } from 'zod';
import { 
  ComponentList, 
  ComponentDialog, 
  FormFields, 
  initialComponentState,
  formSchema
} from "@/components/Form";

const FormEdit = () => {
  const navigate = useNavigate();
  const { formSlug } = useParams();
  const { axiosInstance } = useAuth();
  const { toast } = useToast();
  
  // Helper functions for form URL
  const getFormBaseUrl = () => {
    return import.meta.env.VITE_FORMS_FRONTEND_BASE_URL || 'https://forms.status.bot';
  };
  
  const getFormDisplayDomain = () => {
    const baseUrl = getFormBaseUrl();
    return baseUrl.replace(/^https?:\/\//, '');
  };
  
  const getFormFullUrl = (slug) => {
    const baseUrl = getFormBaseUrl();
    return `${baseUrl}/${slug}`;
  };
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    password: '',
    organization: '',
    components: [],
    online: true
  });
  
  const [loading, setLoading] = useState(false);
  const [fetchingForm, setFetchingForm] = useState(true);
  const [showComponentDialog, setShowComponentDialog] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [originalSlug, setOriginalSlug] = useState('');
  const [originalFormData, setOriginalFormData] = useState(null);
  
  // Slug validation states
  const [slugValidating, setSlugValidating] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [slugChecked, setSlugChecked] = useState(false);
  
  // Component errors state
  const [errors, setErrors] = useState({});
  
  // Component to add/edit
  const [componentToAdd, setComponentToAdd] = useState(initialComponentState);
  
  // Check if there are changes
  const hasChanges = originalFormData ? (
    formData.name !== originalFormData.name ||
    formData.password !== originalFormData.password ||
    formData.organization !== originalFormData.organization ||
    formData.online !== originalFormData.online ||
    JSON.stringify(formData.components) !== JSON.stringify(originalFormData.components)
  ) : false;
  
  // Fetch form data
  useEffect(() => {
    const fetchFormData = async () => {
      setFetchingForm(true);
      try {
        const response = await axiosInstance.get(`/forms/edit/${formSlug}`);
        const form = response.data;
        
        const formDataToSet = {
          _id: form._id,
          name: form.name,
          slug: form.slug,
          password: form.password,
          organization: form.organization._id,
          components: form.components,
          online: form.online !== undefined ? form.online : true
        };
        
        setFormData(formDataToSet);
        setOriginalFormData(formDataToSet);
        
        setOriginalSlug(form.slug);
        setSlugChecked(true);
        setSlugAvailable(true);
      } catch (error) {
        console.error('Error fetching form:', error);
        toast({
          title: "Error",
          description: "Failed to load form data",
          variant: "destructive"
        });
        navigate('/forms');
      } finally {
        setFetchingForm(false);
      }
    };
    
    fetchFormData();
  }, [formSlug, axiosInstance, navigate, toast]);
  
  // Debounce function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };
  
  // Check if slug is available
  const checkSlugAvailability = useCallback(
    debounce(async (slug) => {
      if (!slug) {
        setSlugChecked(false);
        setSlugAvailable(null);
        return;
      }
      
      // If slug hasn't changed, it's available
      if (slug === originalSlug) {
        setSlugChecked(true);
        setSlugAvailable(true);
        setFormErrors(prev => ({
          ...prev,
          slug: null
        }));
        return;
      }
      
      setSlugValidating(true);
      setSlugChecked(false);
      
      try {
        const response = await axiosInstance.get(`/forms/check-slug/${slug}`);
        setSlugAvailable(response.data.available);
        setSlugChecked(true);
        
        // Update form errors if needed
        if (!response.data.available) {
          setFormErrors(prev => ({
            ...prev,
            slug: 'This form URL is already taken'
          }));
        } else {
          setFormErrors(prev => ({
            ...prev,
            slug: null
          }));
        }
      } catch (error) {
        console.error('Error checking slug:', error);
        setSlugAvailable(null);
      } finally {
        setSlugValidating(false);
      }
    }, 500),
    [axiosInstance, originalSlug]
  );
  
  // Validate slug when it changes
  useEffect(() => {
    if (formData.slug) {
      checkSlugAvailability(formData.slug);
    } else {
      setSlugChecked(false);
      setSlugAvailable(null);
    }
  }, [formData.slug, checkSlugAvailability]);
  
  const handleFormChange = (field, value) => {
    // Clear error when field changes
    setFormErrors({
      ...formErrors,
      [field]: null
    });
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Reset slug validation if needed
    if (field === 'slug') {
      setSlugChecked(false);
    }
  };
  
  const handleComponentChange = (field, value, resetForm = false) => {
    // Clear errors when user changes a field
    setErrors({
      ...errors,
      [field]: null
    });
    
    if (resetForm) {
      // Reset the form when changing type but keep the new type
      setComponentToAdd({
        ...initialComponentState,
        type: value
      });
    } else {
      // Convert numeric fields to numbers
      if (field === 'minLength' || field === 'maxLength') {
        value = value === '' ? '' : Number(value);
      }
      
      setComponentToAdd(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };
  
  const resetComponentForm = () => {
    setComponentToAdd(initialComponentState);
    setErrors({});
    setIsEditing(false);
    setEditingIndex(null);
  };
  
  const validateComponent = () => {
    const newErrors = {};
    
    // Common validation
    if (componentToAdd.type !== 'markdown' && !componentToAdd.name.trim()) {
      newErrors.name = 'Label is required';
    }
    
    // Type-specific validation
    if (componentToAdd.type === 'markdown' && !componentToAdd.content.trim()) {
      newErrors.content = 'Content is required';
    }
    
    if (componentToAdd.type === 'select' && 
        (!componentToAdd.options || componentToAdd.options.length === 0)) {
      newErrors.options = 'At least one option is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const addComponent = () => {
    if (!validateComponent()) {
      return false;
    }
    
    // Clean up fields based on component type
    const newComponent = { ...componentToAdd };
    
    // Only keep relevant fields for the component type
    if (newComponent.type !== 'markdown') {
      delete newComponent.content;
    } else {
      delete newComponent.name;
      delete newComponent.placeholder;
    }
    
    if (newComponent.type !== 'select') {
      delete newComponent.options;
    }
    
    // Remove placeholder for file uploads
    if (newComponent.type === 'file') {
      delete newComponent.placeholder;
      delete newComponent.rememberValue;
    } else {
      // Remove file-specific fields for non-file components
      delete newComponent.maxFiles;
    }
    
    if (isEditing && editingIndex !== null) {
      // Update existing component
      const updatedComponents = [...formData.components];
      updatedComponents[editingIndex] = newComponent;
      
      setFormData(prev => ({
        ...prev,
        components: updatedComponents
      }));
      
      toast({
        title: "Success",
        description: "Component updated successfully"
      });
    } else {
      // Add new component
      setFormData(prev => ({
        ...prev,
        components: [...prev.components, newComponent]
      }));
    }
    
    // Reset and close
    resetComponentForm();
    setShowComponentDialog(false);
    
    return true;
  };
  
  const removeComponent = (index) => {
    const updatedComponents = [...formData.components];
    updatedComponents.splice(index, 1);
    
    setFormData({
      ...formData,
      components: updatedComponents
    });
  };
  
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(formData.components);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setFormData({
      ...formData,
      components: items
    });
  };
  
  const validateForm = () => {
    try {
      formSchema.parse(formData);
      
      // For edit form, no need to check slug availability since it's not editable
      setFormErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors = {};
        error.errors.forEach((err) => {
          const path = err.path.join('.');
          errors[path] = err.message;
        });
        setFormErrors(errors);
        return false;
      }
      return false;
    }
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) {
      // Show toast with validation errors
      toast({
        description: "Please fix the errors in the form",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    
    try {
      // Since server ignores slug changes, we ensure we're using the original slug
      // in the UI by navigating back to the correct page
      const submitData = {
        ...formData,
        slug: originalSlug // Use original slug, server ignores any changes anyway
      };
      
      await axiosInstance.put(`/forms/${formData._id}`, submitData);
      toast({
        description: "Form updated successfully"
      });
      navigate(`/forms`);
    } catch (error) {
      console.error('Error updating form:', error);
      toast({
        description: error.response?.data?.message || 'Error updating form',
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDiscardChanges = () => {
    if (originalFormData) {
      setFormData({ ...originalFormData });
      setFormErrors({});
      toast({
        description: "Changes discarded"
      });
    }
  };
  
  const editComponent = (index) => {
    const component = formData.components[index];
    
    // Populate the form with component data
    setComponentToAdd({
      ...initialComponentState,
      ...component
    });
    
    setIsEditing(true);
    setEditingIndex(index);
    setShowComponentDialog(true);
  };
  
  // Loading state component
  const LoadingState = () => (
    <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center space-x-4 animate-pulse">
        <AiOutlineLoading3Quarters className="text-2xl text-primary animate-spin" />
        <h1 className="text-xl font-semibold">Loading your form</h1>
    </div>
  );

  const fadeInVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 }
  };
  
  if (fetchingForm) {
    return (
      <Layout>
        <LoadingState />
      </Layout>
    );
  }
  
  return (
    <Layout>
      <motion.div
        variants={fadeInVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.5 }}
        className="space-y-6 pb-6"
      >
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex-col items-center space-y-2">
            <div className="flex items-center space-x-4">
              <div 
                className="flex items-center space-x-4 cursor-pointer text-3xl group"
                onClick={() => navigate('/forms')}
              >
                <GoArrowLeft className="group-hover:text-violet-500 transition-colors" />
                <div className="flex items-center space-x-3">
                  <h1 className="-mt-0.5 font-semibold">{formData.name || 'Edit Form'}</h1>
                  {formData.online ? (
                    <Badge variant="outline" className="border-emerald-200 text-emerald-700 dark:border-emerald-700 dark:text-emerald-300 text-xs px-3 py-1 flex items-center space-x-1">
                      <FaCircle className="h-2 w-2" />
                      <span>Online</span>
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-muted-foreground/30 text-muted-foreground text-xs px-3 py-1 flex items-center space-x-1">
                      <FiCircle className="h-2 w-2" />
                      <span>Offline</span>
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2 pl-12">
              <a 
                href={getFormFullUrl(formData.slug)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground -mt-0.5 hover:text-primary flex items-center gap-1 transition-colors"
              >
                {getFormDisplayDomain()}/{formData.slug}
                <FiExternalLink className="h-3 w-3" />
              </a>
              <span className="text-muted-foreground">•</span>
              <span className="text-sm text-muted-foreground">
                {formData.components.length} component{formData.components.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {hasChanges && (
              <>
                <Button 
                  onClick={handleDiscardChanges}
                  variant="outline"
                >
                  Discard Changes
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={loading || formData.components.length === 0}
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin mr-2"></div>
                      Saving...
                    </div>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Main Content */}
        <motion.div
          variants={fadeInVariants}
          transition={{ delay: 0.2 }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form Settings - Left Column */}
            <div className="lg:col-span-1">
              <div className="sticky top-6">
                <div className="bg-card rounded-xl border border-border/50 p-6 shadow-sm">
                  <div className="flex items-center space-x-2 mb-6">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <FiSettings className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <h2 className="font-semibold">Form Settings</h2>
                      <p className="text-sm text-muted-foreground">Configure your form details</p>
                    </div>
                  </div>
                  
                  <FormFields 
                    formData={formData}
                    handleFormChange={handleFormChange}
                    formErrors={formErrors}
                    slugValidating={slugValidating}
                    slugChecked={slugChecked}
                    slugAvailable={slugAvailable}
                    disabledFields={{ slug: true }}
                  />
                </div>
              </div>
            </div>
            
            {/* Form Builder - Right Column */}
            <div className="lg:col-span-2">
              <div className="bg-card rounded-xl border border-border/50 shadow-sm">
                <div className="p-6 border-b border-border/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <FiLayers className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <h2 className="font-semibold">Form Components</h2>
                        <p className="text-sm text-muted-foreground">
                          {formData.components.length} component{formData.components.length !== 1 ? 's' : ''} configured
                        </p>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={() => {
                        resetComponentForm();
                        setShowComponentDialog(true);
                      }}
                      size="sm"
                      className="flex items-center"
                      variant="secondary"
                    >
                      <AiOutlinePlus className="h-4 w-4 mr-2" />
                      Add Component
                    </Button>
                  </div>
                </div>
                
                <div className="p-4">
                  <ComponentList 
                    components={formData.components}
                    onDragEnd={handleDragEnd}
                    onEditComponent={editComponent}
                    onRemoveComponent={removeComponent}
                    formErrors={formErrors}
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        
        <ComponentDialog 
          open={showComponentDialog}
          onOpenChange={(open) => {
            if (!open) resetComponentForm();
            setShowComponentDialog(open);
          }}
          componentData={componentToAdd}
          onComponentChange={handleComponentChange}
          onAddComponent={addComponent}
          isEditing={isEditing}
          errors={errors}
        />
      </motion.div>
      
      {loading && <LoadingState />}
    </Layout>
  );
};

export default FormEdit; 